import groq from "groq";
import { TargetAudienceCode } from "../../targetAudiences";
import { ResolvedListSummary, resolveListSummary } from "./resolveList";
import { ResolvedRecommendationSummary, resolveRecommendationSummary } from "../../recommendations";
import { orderListsAndRecommendations } from "../../promotion";
import { z } from "zod";

export const listsAndRecommendationsUrlStateSchema = z
  .object({
    targetAudiences: z.array(z.string()).optional(),
    pagination: z.number().default(1).optional(),
  })
  .optional();

export type ListsAndRecommendationsUrlState = z.infer<typeof listsAndRecommendationsUrlStateSchema>;

export const listsAndRecommendationsPageSize = 10 as const;

export type ListsAndRecommendationsResponse = {
  listsAndRecommendations: (ResolvedListSummary | ResolvedRecommendationSummary)[];
  endOfResults: boolean;
  facets: ListsAndRecommendationsFacets;
};

export type ListsAndRecommendationsFacets = {
  targetAudiences: TargetAudienceCode[];
};

export type ListsAndRecommendationsFacetName = keyof ListsAndRecommendationsFacets;

export type ListsAndRecommendationsGroqParams = {
  targetAudiences: string[] | null;
  start: number;
  end: number;
};

export const getListsAndRecommendationsGroqParams = (
  urlState: ListsAndRecommendationsUrlState,
  page: number
): ListsAndRecommendationsGroqParams => ({
  targetAudiences: urlState?.targetAudiences || null,
  start: (page - 1) * listsAndRecommendationsPageSize,
  end: page * listsAndRecommendationsPageSize,
});

const listsAndRecommendationsFacetsFilters = groq`[
  (!defined($targetAudiences) || length($targetAudiences) == 0 || array::intersects(targetAudiences, $targetAudiences))
]`;

export const listsAndRecommendationsQuery = groq`{
    "filteredListsAndRecommendations": *[(_type == "list" || _type == "recommendation")] | ${orderListsAndRecommendations}
    ${listsAndRecommendationsFacetsFilters},
  }
  {
    "listsAndRecommendations": @.filteredListsAndRecommendations [$start...$end] {
      _type == "list" => {${resolveListSummary}},
      _type == "recommendation" => {${resolveRecommendationSummary}}
    },
    "endOfResults": !defined(@.filteredListsAndRecommendations[$end]),
    "facets": {
      "targetAudiences": array::unique(@.filteredListsAndRecommendations[].targetAudiences[]) [defined(@)],
    }
  }`;
